import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import * as api from "../api";
import useLiveData from "../realtime/useLiveData";
import { useCompany } from "./CompanyContext";

const ShiftContext = createContext();

export function useShift() {
  const context = useContext(ShiftContext);
  if (!context) {
    throw new Error("useShift must be used within a ShiftProvider");
  }
  return context;
}

export function ShiftProvider({ children }) {
  const { company } = useCompany();

  // Master switch (Settings → Shift Management), read directly from the
  // shared server-backed Company document.
  const shiftEnabled = useMemo(
    () => Boolean(company?.enableShiftTracking),
    [company?.enableShiftTracking]
  );

  const [myActiveShift, setMyActiveShift] = useState(null);
  const [activeShiftsList, setActiveShiftsList] = useState([]);
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [pendingAddToCart, setPendingAddToCart] = useState(null);
  const [shiftLoading, setShiftLoading] = useState(false);

  const fetchMyActiveShift = useCallback(async () => {
    try {
      const { data } = await api.getMyActiveShift();
      setMyActiveShift(data || null);
      return data;
    } catch (err) {
      setMyActiveShift(null);
      return null;
    }
  }, []);

  const fetchActiveShifts = useCallback(async () => {
    try {
      const { data } = await api.getActiveShifts();
      setActiveShiftsList(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      setActiveShiftsList([]);
      return [];
    }
  }, []);

  // Live: a shift started / ended on another till (or this user's shift
  // ended elsewhere) is reflected here without a refresh.
  useLiveData(["shifts"], () => {
    if (!shiftEnabled) return;
    fetchMyActiveShift();
    fetchActiveShifts();
  });

  const startShift = useCallback(async (employeeOrPayload, openSnackbar) => {
    setShiftLoading(true);
    try {
      const profile = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("profile") || "{}") : {};
      const userName = profile?.result?.name || profile?.result?.email || "User";
      const payload =
        employeeOrPayload && typeof employeeOrPayload === "object" && employeeOrPayload._id
          ? { employeeId: employeeOrPayload._id, employeeName: employeeOrPayload.name, userName }
          : typeof employeeOrPayload === "object"
          ? { ...employeeOrPayload, userName }
          : { userName: employeeOrPayload };
      const { data } = await api.startShift(payload);
      setMyActiveShift(data);
      await fetchActiveShifts();
      if (openSnackbar) openSnackbar("Shift started successfully");
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to start shift";
      if (openSnackbar) openSnackbar(msg);
      return null;
    } finally {
      setShiftLoading(false);
    }
  }, [fetchActiveShifts]);

  const endShift = useCallback(async (openSnackbar) => {
    setShiftLoading(true);
    try {
      const { data } = await api.endShift();
      setMyActiveShift(null);
      await fetchActiveShifts();
      if (openSnackbar) openSnackbar("Shift ended successfully");
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to end shift";
      if (openSnackbar) openSnackbar(msg);
      return null;
    } finally {
      setShiftLoading(false);
    }
  }, [fetchActiveShifts]);

  // Selling is only ever blocked while shift tracking is switched on.
  const isShiftBlocked = shiftEnabled && !myActiveShift;

  const openShiftRequiredDialog = useCallback(
    (item, quantity) => {
      // Defensive: never surface the "start a shift" prompt when the feature is off.
      if (!shiftEnabled) return;
      setPendingAddToCart({ item, quantity });
      setShowShiftDialog(true);
    },
    [shiftEnabled]
  );

  const closeShiftDialog = useCallback(() => {
    setShowShiftDialog(false);
    setPendingAddToCart(null);
  }, []);

  // All the callbacks above are already useCallback-stable, so memoizing
  // this only produces a new object (re-rendering every consumer) when a
  // state value actually changes, instead of on every ShiftProvider render.
  const value = useMemo(
    () => ({
      shiftEnabled,
      isShiftBlocked,
      myActiveShift,
      setMyActiveShift,
      activeShiftsList,
      setActiveShiftsList,
      showShiftDialog,
      setShowShiftDialog,
      pendingAddToCart,
      setPendingAddToCart,
      shiftLoading,
      fetchMyActiveShift,
      fetchActiveShifts,
      startShift,
      endShift,
      openShiftRequiredDialog,
      closeShiftDialog,
    }),
    [
      shiftEnabled,
      isShiftBlocked,
      myActiveShift,
      activeShiftsList,
      showShiftDialog,
      pendingAddToCart,
      shiftLoading,
      fetchMyActiveShift,
      fetchActiveShifts,
      startShift,
      endShift,
      openShiftRequiredDialog,
      closeShiftDialog,
    ]
  );

  return (
    <ShiftContext.Provider value={value}>
      {children}
    </ShiftContext.Provider>
  );
}
